import { useEffect, useRef } from 'react';

// A block that never mounts — filtered out of the list, or deleted while the
// request was in flight — would keep the loop below alive, so give up.
const REALIGN_DEADLINE_MS = 500;

/**
 * Scrolls the virtualized block list to a block, and corrects the landing once
 * the rows involved have reported their real heights.
 *
 * Why the correction is needed: `VariableSizeList` positions rows from
 * `itemSize(index)`, which only knows a real height for rows that have already
 * rendered — every other row contributes an estimate. The first scroll therefore
 * lands on an offset built partly from estimates, and as the rows around the
 * target mount and measure themselves the model shifts underneath, moving the
 * target away from where it was put.
 *
 * So: scroll once, then wait for measurements to stop arriving and align a final
 * time. Waiting on that signal rather than on a fixed timer is what makes it hold
 * for a project with hundreds of blocks, where the first landing is furthest off.
 *
 * The caller sets `scrollToBlockId` as a one-shot request and clears it shortly
 * after; the id is latched here so that clearing does not abandon the correction.
 * Nothing re-scrolls for a request already served — a height change elsewhere
 * (someone typing) must never yank the view back.
 *
 * @param {{ listRef: {current: object},
 *           blockIds: string[],
 *           rowHeights: {current: Record<string, number>},
 *           scrollToBlockId: string | null,
 *           align: 'auto' | 'smart' | 'center' | 'start' | 'end' }} params
 */
const useScrollToBlock = ({
  listRef,
  blockIds,
  rowHeights,
  scrollToBlockId,
  align = 'center',
}) => {
  const servedRef = useRef(null);

  useEffect(() => {
    if (!scrollToBlockId) {
      // Request withdrawn: let the same block be requested again later.
      servedRef.current = null;
      return undefined;
    }
    if (servedRef.current === scrollToBlockId) return undefined;

    const index = blockIds.indexOf(scrollToBlockId);
    if (index === -1 || !listRef.current) return undefined;

    servedRef.current = scrollToBlockId;
    listRef.current.scrollToItem(index, align);

    // The number of measured rows only grows as rows mount, so a frame that adds
    // none means the burst triggered by this scroll has settled.
    let frame = null;
    let lastMeasuredCount = -1;
    const deadline = Date.now() + REALIGN_DEADLINE_MS;

    const realign = () => listRef.current?.scrollToItem(index, align);

    const tick = () => {
      const measuredCount = Object.keys(rowHeights.current).length;
      const targetMeasured = rowHeights.current[scrollToBlockId] !== undefined;

      if (targetMeasured && measuredCount === lastMeasuredCount) {
        realign();
        return;
      }
      if (Date.now() > deadline) {
        if (targetMeasured) realign();
        return;
      }
      lastMeasuredCount = measuredCount;
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scrollToBlockId, align, blockIds, listRef, rowHeights]);
};

export default useScrollToBlock;
